import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody } from '../components/ui';
import { subscribeEvents } from '../lib/firestore';
import { eventPrimaryUrl, eventTypeLabel, formatEventRange } from '../lib/eventUtils';
import type { Event } from '../lib/types';

export default function EventsPage() {
  const nav = useNavigate();
  const [events, setEvents] = React.useState<Event[]>([]);

  React.useEffect(() => {
    const unsub = subscribeEvents(setEvents);
    return () => unsub();
  }, []);

  const upcomingEvents = React.useMemo(() => events.filter((event) => event.endAt >= Date.now()), [events]);

  return (
    <div className="grid grid-cols-1 gap-4">
      <Card className="overflow-hidden">
        <div className="flex items-start justify-between gap-3 border-b border-zinc-300 bg-zinc-200 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-zinc-900">Events</div>
            <div className="mt-0.5 truncate text-xs text-zinc-600">Upcoming Wild Apricot events with Hub discussion</div>
          </div>
        </div>
        <CardBody className="space-y-3">
          {upcomingEvents.length === 0 ? <div className="text-sm text-zinc-600">No upcoming events</div> : null}

          {upcomingEvents.map((e) => {
            const actionUrl = eventPrimaryUrl(e);
            return (
            <div
              key={e.id}
              role="link"
              tabIndex={0}
              onClick={() => nav(`/events/${e.id}`)}
              onKeyDown={(evt) => {
                if (evt.key === 'Enter' || evt.key === ' ') {
                  evt.preventDefault();
                  nav(`/events/${e.id}`);
                }
              }}
              className="cursor-pointer rounded-2xl border border-zinc-200 bg-white p-3 transition hover:bg-zinc-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{e.title}</div>
                  <div className="mt-2 line-clamp-2 whitespace-pre-wrap text-sm text-zinc-700">{e.description}</div>
                  <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-600">
                    <span>{formatEventRange(e.startAt, e.endAt)}</span>
                    {e.location ? <span>{e.location}</span> : null}
                    <span>{eventTypeLabel(e.type)}</span>
                  </div>
                </div>
                {actionUrl ? (
                  <a
                    className="inline-flex shrink-0 rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50"
                    href={actionUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(evt) => evt.stopPropagation()}
                  >
                    Register
                  </a>
                ) : null}
              </div>
            </div>
            );
          })}
        </CardBody>
      </Card>
    </div>
  );
}
